import { useState, useCallback, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useModuloDetail } from '@hooks/useModuloDetail.js'
import { useTopico } from '@hooks/useTopico.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { PassoTopSkeleton } from '@components/primitives/Skeleton.js'
import { ROUTES } from '@utils/constants.js'
import {
  getPassoByTopicoId,
  getPassoAnterior,
  getPassoProximo,
} from '@content/jornada.js'
import { PassoNavBar } from '@components/passo/PassoNavBar.js'
import { PassoTabs, type PassoTabId } from '@components/passo/PassoTabs.js'
import { PassoAgoraSection } from '@components/passo/PassoAgoraSection.js'
import { PassoAlertasSection } from '@components/passo/PassoAlertasSection.js'
import { PassoFazerSection } from '@components/passo/PassoFazerSection.js'
import { PassoRegistrarSection } from '@components/passo/PassoRegistrarSection.js'
import { PageShell } from '@components/layout/PageShell.js'
import { PassoDecisaoBanner } from '@components/guia/PassoDecisaoBanner.js'
import { getTopicoTipo } from '@utils/topicoTipo.js'
import { formatPassoNoModulo } from '@utils/passoLabel.js'

export function TopicoPage() {
  const navigate = useNavigate()
  const { moduloId = '', topicoId = '' } = useParams<{ moduloId: string; topicoId: string }>()
  const { modulo, isLoading: moduloLoading } = useModuloDetail(moduloId)
  const {
    content,
    progresso,
    diario,
    checklistItems,
    percentualPratico,
    isLoading: topicoLoading,
    toggleChecklistItem,
    saveMetadado,
    addEntradaDiario,
    marcarConcluido,
    desmarcarConcluido,
    marcarTodosChecklist,
    editEntradaDiario,
    deleteEntradaDiario,
  } = useTopico(moduloId, topicoId)

  const [tab, setTab] = useState<PassoTabId>('agir')
  const [novaEntrada, setNovaEntrada] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [metaLocal, setMetaLocal] = useState<Record<string, string>>({})

  const passo = getPassoByTopicoId(topicoId)
  const anterior = getPassoAnterior(topicoId)
  const proximo = getPassoProximo(topicoId)
  const topico = modulo?.topicos.find((t) => t.id === topicoId)
  const jaConcluido = progresso?.concluido ?? false
  const tipo = topico ? getTopicoTipo(moduloId, topico.slug, topicoId) : 'conteudo'
  const temFerramenta = tipo === 'ferramenta' || (content?.temFerramenta ?? false)

  const getMetaValue = useCallback(
    (campo: string) => metaLocal[campo] ?? progresso?.metadados?.[campo] ?? '',
    [metaLocal, progresso],
  )

  const handleSalvarMetadado = useCallback(
    async (campo: string, v: string) => {
      setMetaLocal((prev) => ({ ...prev, [campo]: v }))
      await saveMetadado(campo, v)
    },
    [saveMetadado],
  )

  const handleSalvarEntrada = useCallback(async () => {
    if (!novaEntrada.trim()) return
    setSalvando(true)
    await addEntradaDiario(novaEntrada.trim())
    setNovaEntrada('')
    setSalvando(false)
  }, [novaEntrada, addEntradaDiario])

  const handleToggleConcluido = useCallback(async () => {
    if (jaConcluido) await desmarcarConcluido()
    else await marcarConcluido()
  }, [jaConcluido, marcarConcluido, desmarcarConcluido])

  const passoLabel = passo ? formatPassoNoModulo(passo) : undefined

  const tabs = useMemo(() => {
    if (!topico) return []

    const resumo = content?.resumo ?? []
    const metadados = content?.metadados ?? []

    const agirPanel = (
      <>
        {resumo[0] && (
          <PassoDecisaoBanner
            decisao={resumo[0]}
            dicas={content?.dicasDeOuro?.length ?? 0}
            alertas={content?.atencao?.length ?? 0}
            ferramenta={temFerramenta}
            checklist={checklistItems.length}
          />
        )}
        <PassoAgoraSection resumo={resumo.slice(1)} passoLabel={passoLabel} nested />
        {content && (
          <PassoAlertasSection dicas={content.dicasDeOuro} atencao={content.atencao} nested />
        )}
        <PassoFazerSection
          topicoId={topicoId}
          topicoSlug={topico.slug}
          moduloId={moduloId}
          temFerramenta={temFerramenta}
          checklistItems={checklistItems}
          nested
        />
      </>
    )

    const registrarPanel = (
      <PassoRegistrarSection
        metadados={metadados}
        getMetaValue={getMetaValue}
        onSaveMeta={handleSalvarMetadado}
        diario={diario}
        novaEntrada={novaEntrada}
        onNovaEntradaChange={setNovaEntrada}
        onSalvarEntrada={handleSalvarEntrada}
        salvando={salvando}
        onEditEntrada={editEntradaDiario}
        onDeleteEntrada={deleteEntradaDiario}
        nested
        passoTitulo={topico.titulo}
        moduloId={moduloId}
        topicoId={topicoId}
        temFerramenta={temFerramenta}
        checklistItems={checklistItems}
        percentualPratico={percentualPratico}
        onToggleChecklist={toggleChecklistItem}
        onMarcarTodos={marcarTodosChecklist}
      />
    )

    return [
      { id: 'agir' as const, label: 'Agir', panel: agirPanel },
      { id: 'registrar' as const, label: 'Registrar', panel: registrarPanel },
    ]
  }, [
    topico,
    passoLabel,
    content,
    topicoId,
    temFerramenta,
    checklistItems,
    percentualPratico,
    toggleChecklistItem,
    marcarTodosChecklist,
    getMetaValue,
    handleSalvarMetadado,
    diario,
    novaEntrada,
    handleSalvarEntrada,
    salvando,
    editEntradaDiario,
    deleteEntradaDiario,
    moduloId,
  ])

  if (moduloLoading || topicoLoading) {
    return (
      <PageShell variant='passo' className='cf3-page--passo'>
        <PassoTopSkeleton label='Carregando etapa…' />
      </PageShell>
    )
  }

  if (!modulo || !topico) {
    return (
      <PageShell variant='passo'>
        <EmptyPanel
          title='Etapa não encontrada'
          description='Esta etapa não existe na jornada ou foi removida.'
          actionLabel='Voltar à jornada'
          onAction={() => navigate(ROUTES.MODULOS)}
        />
      </PageShell>
    )
  }

  return (
    <PageShell variant='passo' className='cf3-page--passo'>
      <header className='cf3-passo-top'>
        <nav className='cf3-passo-top__trail' aria-label='Localização'>
          <Link to={ROUTES.MODULOS}>Jornada</Link>
          <span aria-hidden>·</span>
          <span>{modulo.titulo}</span>
          <span aria-hidden>·</span>
          {passoLabel && <span>{passoLabel}</span>}
        </nav>
        <h1 className='cf3-passo-top__title'>{topico.titulo}</h1>
        {jaConcluido && <span className='cf3-passo-top__badge'>Concluído</span>}
      </header>

      <PassoTabs tabs={tabs} active={tab} onChange={setTab} />

      <PassoNavBar
        anterior={anterior}
        proximo={proximo}
        jaConcluido={jaConcluido}
        onToggleConcluido={handleToggleConcluido}
        salvando={salvando}
      />
    </PageShell>
  )
}
